import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ContractTemplateCollectionRecord } from '@shared/types';
import { BulkEditorRail } from '@renderer/shared/components/bulk-editor-rail';
import {
  CollectionSelectionToggle,
  CollectionToolbar,
  DataTableSurface,
  EmptyCollectionState
} from '@renderer/shared/components/collection-scaffold';
import { CollectionTable } from '@renderer/shared/components/collection-table';
import { Icon } from '@renderer/shared/components/icons';
import { SplitWorkspaceLayout } from '@renderer/shared/components/split-workspace';
import {
  FilterChip,
  OverflowMenuButton,
  SectionHeader,
  SelectField,
  StatusBadge,
  ToolbarGroup
} from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { listContractTemplates } from './contract-templates-repository';
import {
  selectContractTemplateRows,
  selectContractTemplatesSummary,
  selectContractTemplateSelectionItems
} from './contract-templates-selectors';
import { useContractTemplatesViewStore } from './contract-templates-view-store';

export function ContractTemplatesScreen(): JSX.Element {
  const navigate = useNavigate();
  const { locale, t } = useI18n();
  const [collection, setCollection] = useState<ContractTemplateCollectionRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {
    statusFilter,
    categoryFilter,
    languageFilter,
    sort,
    selectionSession,
    setStatusFilter,
    setCategoryFilter,
    setLanguageFilter,
    setSort,
    syncSelectionQuery,
    toggleSelection,
    removeSelectedId,
    clearSelection
  } = useContractTemplatesViewStore();

  useEffect(() => {
    let active = true;

    void listContractTemplates()
      .then((nextCollection) => {
        if (active) {
          setCollection(nextCollection);
        }
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Contract templates unavailable.');
        }
      });

    return () => {
      active = false;
      clearSelection();
    };
  }, [clearSelection]);

  const summary = useMemo(() => (collection ? selectContractTemplatesSummary(collection) : null), [collection]);
  const rows = useMemo(() => {
    if (!collection) {
      return [];
    }

    return selectContractTemplateRows(collection, {
      locale,
      statusFilter,
      categoryFilter,
      languageFilter,
      sort,
      t
    });
  }, [categoryFilter, collection, languageFilter, locale, sort, statusFilter, t]);
  const selectionItems = useMemo(() => (collection ? selectContractTemplateSelectionItems(collection) : []), [collection]);
  const visibleIds = useMemo(() => rows.map((row) => row.id), [rows]);
  const visibleIdsKey = visibleIds.join('|');
  const queryKey = `${statusFilter}|${categoryFilter}|${languageFilter}|${sort}`;

  useEffect(() => {
    syncSelectionQuery(queryKey, visibleIds);
  }, [queryKey, syncSelectionQuery, visibleIds, visibleIdsKey]);

  if (error) {
    return (
      <div className="av-page">
        <EmptyCollectionState title={t('contracts.templates.title')} description={error} />
      </div>
    );
  }

  if (!collection || !summary) {
    return <div className="av-page" />;
  }

  const selectedItems = selectionSession.selectedIds
    .map((selectedId) => selectionItems.find((item) => item.id === selectedId) ?? null)
    .filter((item): item is NonNullable<typeof item> => item !== null);

  function handleToggleSelection(templateId: string, event: MouseEvent<HTMLButtonElement>): void {
    event.preventDefault();
    event.stopPropagation();
    toggleSelection(templateId, visibleIds, queryKey, {
      additive: true,
      range: event.shiftKey
    });
  }

  return (
    <div className="av-page">
      <SectionHeader
        title={t('contracts.templates.title')}
        subtitle={`${t('contracts.templates.summary.active', { count: summary.activeCount })} | ${t('contracts.templates.summary.draft', { count: summary.draftCount })} | ${t('contracts.templates.summary.archived', { count: summary.archivedCount })}`}
        actions={
          <div className="av-toolbar-group">
            <button className="av-secondary-button" type="button" onClick={() => navigate('/contracts')}>
              {t('contracts.templates.actions.backToContracts')}
            </button>
            <button className="av-primary-button" type="button" onClick={() => navigate('/contracts/templates/new')}>
              {t('contracts.templates.actions.new')}
            </button>
          </div>
        }
      />

      <CollectionToolbar
        primary={
          <ToolbarGroup>
            {(['all', 'draft', 'active', 'archived'] as const).map((filter) => (
              <FilterChip
                key={filter}
                active={statusFilter === filter}
                label={t(`contracts.templates.filters.${filter}`)}
                onClick={() => setStatusFilter(filter)}
              />
            ))}
          </ToolbarGroup>
        }
        secondary={
          <ToolbarGroup>
            <SelectField
              options={[
                { value: 'all', label: t('contracts.templates.filters.allCategories') },
                { value: 'service_agreement', label: t('enums.contractTemplateCategory.service_agreement') },
                { value: 'website_development', label: t('enums.contractTemplateCategory.website_development') },
                { value: 'maintenance', label: t('enums.contractTemplateCategory.maintenance') },
                { value: 'marketing', label: t('enums.contractTemplateCategory.marketing') },
                { value: 'hosting', label: t('enums.contractTemplateCategory.hosting') },
                { value: 'consulting', label: t('enums.contractTemplateCategory.consulting') },
                { value: 'nda', label: t('enums.contractTemplateCategory.nda') },
                { value: 'annex', label: t('enums.contractTemplateCategory.annex') }
              ]}
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value as typeof categoryFilter)}
            />
            <SelectField
              options={[
                { value: 'all', label: t('contracts.templates.filters.allLanguages') },
                { value: 'ro-RO', label: t('common.languages.ro-RO') },
                { value: 'en-US', label: t('common.languages.en-US') }
              ]}
              value={languageFilter}
              onChange={(event) => setLanguageFilter(event.target.value as typeof languageFilter)}
            />
            <SelectField
              options={[
                { value: 'updated', label: t('contracts.templates.sort.updated') },
                { value: 'name', label: t('contracts.templates.sort.name') },
                { value: 'version', label: t('contracts.templates.sort.version') }
              ]}
              value={sort}
              onChange={(event) => setSort(event.target.value as typeof sort)}
            />
          </ToolbarGroup>
        }
      />

      <SplitWorkspaceLayout
        railMode="fixed"
        main={
          rows.length === 0 ? (
            <EmptyCollectionState title={t('contracts.templates.title')} description={t('common.noResults')} />
          ) : (
            <DataTableSurface>
              <CollectionTable
                ariaLabel={t('contracts.templates.title')}
                columns={[
                  { id: 'selection', label: '', className: 'av-collection-table__selection-cell' },
                  { id: 'template', label: t('contracts.templates.table.template') },
                  { id: 'category', label: t('contracts.templates.table.category') },
                  { id: 'issuer', label: t('contracts.templates.table.issuer') },
                  { id: 'version', label: t('contracts.templates.table.version') },
                  { id: 'actions', label: '', align: 'right' }
                ]}
              >
                {rows.map((row) => {
                  const selected = selectionSession.selectedIds.includes(row.id);

                  return (
                    <tr
                      key={row.id}
                      className={['av-collection-table__row', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}
                    >
                      <td className="av-collection-table__cell av-collection-table__selection-cell">
                        <CollectionSelectionToggle
                          selected={selected}
                          icon="document"
                          label={row.name}
                          onClick={(event) => handleToggleSelection(row.id, event)}
                        />
                      </td>
                      <td className="av-collection-table__cell">
                        <div className="av-collection-table__identity">
                          <div className="av-collection-table__icon">
                            <Icon name="document" />
                          </div>
                          <div className="av-collection-table__identity-copy">
                            <strong>
                              <Link className="av-inline-link" to={`/contracts/templates/${row.id}`}>
                                <span>{row.name}</span>
                              </Link>
                            </strong>
                            <div className="av-inline-metadata">
                              <StatusBadge label={row.statusLabel} tone={row.statusTone} />
                              <span>{row.languageLabel}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="av-collection-table__cell">
                        <div className="av-summary-stack">
                          <strong>{row.categoryLabel}</strong>
                          <span>{row.updatedLabel}</span>
                        </div>
                      </td>
                      <td className="av-collection-table__cell">{row.issuerName}</td>
                      <td className="av-collection-table__cell">{row.versionLabel}</td>
                      <td className="av-collection-table__cell is-right">
                        <OverflowMenuButton
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </CollectionTable>
            </DataTableSurface>
          )
        }
        rail={
          selectedItems.length > 0 ? (
            <BulkEditorRail
              title={t('contracts.templates.selection.title', { count: selectedItems.length })}
              hiddenSelectedCount={selectionSession.hiddenSelectedCount}
              items={selectedItems.map((item) => ({
                id: item.id,
                icon: 'contracts',
                label: item.label,
                subtitle: item.subtitle
              }))}
              onClose={clearSelection}
              onRemoveItem={(itemId) => removeSelectedId(itemId, visibleIds)}
              destructiveAction={{
                label: t('common.clearSelection'),
                onClick: clearSelection
              }}
            >
              <div className="av-bulk-editor-note">{t('contracts.templates.selection.description')}</div>
            </BulkEditorRail>
          ) : null
        }
      />
    </div>
  );
}
